import { useState } from 'react';
import { useDaten } from '../daten/kontext';
import type { KalenderArt } from '../daten/typen';
import { deLang, tagKurz, heute } from '../daten/zeit';
import { Knopf, Feld } from '../ui/bausteine';
import { LehrplanEditor } from './Kurse';

type Bereich = 'kalender' | 'orte' | 'zeiten' | 'fachbereiche' | 'vorlagen' | 'mandant';

export function Stammdaten() {
  const [b, setB] = useState<Bereich>('kalender');
  const NAMEN: Record<Bereich, string> = { kalender: 'Feiertage und Schließtage', orte: 'Standorte und Räume', zeiten: 'Zeitraster', fachbereiche: 'Fachbereiche', vorlagen: 'Lehrgangsvorlagen', mandant: 'Einrichtung' };
  return (
    <div className="seite">
      <header className="seiten-kopf"><h1>Stammdaten</h1><p className="leise">Zentrale Einstellungen, die für alle Kurse gelten</p></header>
      <div className="reiter" role="tablist">
        {(Object.keys(NAMEN) as Bereich[]).map(x => <button key={x} role="tab" aria-selected={b === x} className={b === x ? 'an' : ''} onClick={() => setB(x)}>{NAMEN[x]}</button>)}
      </div>
      <div className="stamm-inhalt">
        {b === 'kalender' && <Kalender />}
        {b === 'orte' && <Orte />}
        {b === 'zeiten' && <Zeiten />}
        {b === 'fachbereiche' && <Fachbereiche />}
        {b === 'vorlagen' && <Vorlagen />}
        {b === 'mandant' && <Einrichtung />}
      </div>
    </div>
  );
}

function Kalender() {
  const { stand, quelle, ausfuehren } = useDaten();
  const [w, setW] = useState({ datum: heute(), art: 'schliesstag' as KalenderArt, bezeichnung: '', standort_id: '' });
  const [jahr, setJahr] = useState(heute().slice(0, 4));
  const jahre = [...new Set(stand.kalender.map(k => k.datum.slice(0, 4)).concat(heute().slice(0, 4)))].sort();
  const liste = stand.kalender.filter(k => k.datum.startsWith(jahr)).sort((a, b) => a.datum.localeCompare(b.datum));
  return (
    <div className="zwei-spalten">
      <div>
        <div className="zeile"><span>Jahr</span><select className="kompakt" value={jahr} onChange={e => setJahr(e.target.value)}>{jahre.map(j => <option key={j}>{j}</option>)}</select></div>
        <ul className="liste-einfach">
          {liste.map(k => (
            <li key={k.id} className="zeile">
              <span>{tagKurz(k.datum)} {deLang(k.datum)}</span><strong>{k.bezeichnung}</strong>
              <span className="tag">{k.art === 'feiertag' ? 'Feiertag' : 'Schließtag'}</span>
              <span className="leise">{k.standort_id ? stand.standorte.find(s => s.id === k.standort_id)?.bezeichnung : 'alle Standorte'}</span>
              <Knopf art="leise" icon="muell" aria-label="Entfernen" onClick={() => ausfuehren(() => quelle.loeschen('kalender_tag', k.id), 'Tag entfernt')} />
            </li>
          ))}
        </ul>
      </div>
      <form className="formular karte" onSubmit={e => { e.preventDefault(); ausfuehren(() => quelle.speichern('kalender_tag', { ...w, standort_id: w.standort_id || null }), 'Tag eingetragen'); }}>
        <h3>Tag sperren</h3>
        <Feld label="Datum"><input type="date" required value={w.datum} onChange={e => setW({ ...w, datum: e.target.value })} /></Feld>
        <Feld label="Art"><select value={w.art} onChange={e => setW({ ...w, art: e.target.value as KalenderArt })}><option value="feiertag">Feiertag</option><option value="schliesstag">Schließtag</option></select></Feld>
        <Feld label="Bezeichnung"><input required value={w.bezeichnung} onChange={e => setW({ ...w, bezeichnung: e.target.value })} placeholder="z. B. Betriebsausflug" /></Feld>
        <Feld label="Gilt für"><select value={w.standort_id} onChange={e => setW({ ...w, standort_id: e.target.value })}><option value="">alle Standorte</option>{stand.standorte.map(s => <option key={s.id} value={s.id}>{s.bezeichnung}</option>)}</select></Feld>
        <p className="leise klein">An gesperrten Tagen lässt das System keine Termine zu.</p>
        <Knopf art="primaer" icon="plus" type="submit">Eintragen</Knopf>
      </form>
    </div>
  );
}

function Orte() {
  const { stand, quelle, ausfuehren, fragen } = useDaten();
  return (
    <div className="karten">
      {stand.standorte.map(s => (
        <article key={s.id} className="karte">
          <input className="titel-eingabe" defaultValue={s.bezeichnung} aria-label="Standort" onBlur={e => e.target.value !== s.bezeichnung && ausfuehren(() => quelle.speichern('standort', { id: s.id, bezeichnung: e.target.value }))} />
          <input defaultValue={s.adresse ?? ''} placeholder="Adresse" aria-label="Adresse" onBlur={e => ausfuehren(() => quelle.speichern('standort', { id: s.id, adresse: e.target.value || null }))} />
          <ul className="liste-einfach">
            {stand.raeume.filter(r => r.standort_id === s.id).map(r => (
              <li key={r.id} className="zeile">
                <input defaultValue={r.bezeichnung} aria-label="Raum" onBlur={e => e.target.value !== r.bezeichnung && ausfuehren(() => quelle.speichern('raum', { id: r.id, bezeichnung: e.target.value }))} />
                <input type="number" className="lp-ue" defaultValue={r.plaetze ?? ''} aria-label="Plätze" title="Plätze" onBlur={e => ausfuehren(() => quelle.speichern('raum', { id: r.id, plaetze: e.target.value ? Number(e.target.value) : null }))} />
                <Knopf art="leise" icon="muell" aria-label="Raum löschen" onClick={async () => { if (await fragen(`Raum „${r.bezeichnung}“ löschen?`, 'Löschen', true)) await ausfuehren(() => quelle.loeschen('raum', r.id)); }} />
              </li>
            ))}
          </ul>
          <Knopf icon="plus" onClick={() => ausfuehren(() => quelle.speichern('raum', { standort_id: s.id, bezeichnung: 'Neuer Raum', plaetze: null, aktiv: true }))}>Raum</Knopf>
        </article>
      ))}
      <article className="karte leer-karte"><Knopf icon="plus" onClick={() => ausfuehren(() => quelle.speichern('standort', { bezeichnung: 'Neuer Standort', adresse: null }))}>Standort anlegen</Knopf></article>
    </div>
  );
}

function Zeiten() {
  const { stand, quelle, ausfuehren } = useDaten();
  return (
    <div className="karten">
      {stand.zeitraster.map(z => (
        <article key={z.id} className="karte">
          <input className="titel-eingabe" defaultValue={z.bezeichnung} aria-label="Zeitraster" onBlur={e => ausfuehren(() => quelle.speichern('zeitraster', { id: z.id, bezeichnung: e.target.value }))} />
          <ul className="liste-einfach">
            {stand.zeitfenster.filter(f => f.zeitraster_id === z.id).sort((a, b) => a.sortierung - b.sortierung).map(f => (
              <li key={f.id} className="zeile">
                <input defaultValue={f.bezeichnung} aria-label="Bezeichnung" onBlur={e => ausfuehren(() => quelle.speichern('zeitfenster', { id: f.id, bezeichnung: e.target.value }))} />
                <input type="time" defaultValue={f.beginn} aria-label="Beginn" onBlur={e => ausfuehren(() => quelle.speichern('zeitfenster', { id: f.id, beginn: e.target.value }))} />
                <input type="time" defaultValue={f.ende} aria-label="Ende" onBlur={e => ausfuehren(() => quelle.speichern('zeitfenster', { id: f.id, ende: e.target.value }))} />
                <span className="leise klein">{f.ue} UE</span>
                <Knopf art="leise" icon="muell" aria-label="Zeitfenster löschen" onClick={() => ausfuehren(() => quelle.loeschen('zeitfenster', f.id))} />
              </li>
            ))}
          </ul>
          <Knopf icon="plus" onClick={() => ausfuehren(() => quelle.speichern('zeitfenster', { zeitraster_id: z.id, bezeichnung: 'Neues Fenster', beginn: '17:30', ende: '20:45', ue: 4, sortierung: 9 }))}>Zeitfenster</Knopf>
          <p className="leise klein">Regel: zwei Doppelstunden à 90 Minuten mit Pause = 4 UE je Einsatz.</p>
        </article>
      ))}
      <article className="karte leer-karte"><Knopf icon="plus" onClick={() => ausfuehren(() => quelle.speichern('zeitraster', { bezeichnung: 'Neues Zeitraster' }))}>Zeitraster anlegen</Knopf></article>
    </div>
  );
}

function Fachbereiche() {
  const { stand, quelle, ausfuehren } = useDaten();
  return (
    <div className="formular schmal">
      <p className="leise">Fachbereiche verbinden Lehrplan und Dozenten: Eingeplant werden kann nur, wer für den Fachbereich hinterlegt ist.</p>
      {stand.fachbereiche.sort((a, b) => a.sortierung - b.sortierung).map(f => (
        <div key={f.id} className="zeile">
          <input type="color" defaultValue={f.farbe} aria-label="Farbe" onBlur={e => ausfuehren(() => quelle.speichern('fachbereich', { id: f.id, farbe: e.target.value }))} />
          <input className="lp-ue" defaultValue={f.kuerzel} aria-label="Kürzel" onBlur={e => ausfuehren(() => quelle.speichern('fachbereich', { id: f.id, kuerzel: e.target.value }))} />
          <input defaultValue={f.bezeichnung} aria-label="Bezeichnung" onBlur={e => ausfuehren(() => quelle.speichern('fachbereich', { id: f.id, bezeichnung: e.target.value }))} />
          <span className="leise klein">{stand.qualifikationen.filter(q => q.fachbereich_id === f.id).length} Dozenten</span>
        </div>
      ))}
      <Knopf icon="plus" onClick={() => ausfuehren(() => quelle.speichern('fachbereich', { bezeichnung: 'Neuer Fachbereich', kuerzel: `FB${stand.fachbereiche.length + 1}`, farbe: '#408FE5', sortierung: stand.fachbereiche.length }))}>Fachbereich anlegen</Knopf>
    </div>
  );
}

function Vorlagen() {
  const { stand, quelle, ausfuehren } = useDaten();
  const [id, setId] = useState(stand.vorlagen[0]?.id ?? '');
  const v = stand.vorlagen.find(x => x.id === id);
  return (
    <div>
      <div className="zeile">
        <select value={id} onChange={e => setId(e.target.value)} aria-label="Vorlage">{stand.vorlagen.map(x => <option key={x.id} value={x.id}>{x.bezeichnung}</option>)}</select>
        <Knopf icon="plus" onClick={async () => { let n = ''; await ausfuehren(async () => { n = await quelle.speichern('lehrgang_vorlage', { bezeichnung: 'Neue Vorlage', standard_wochen: 7, beschreibung: null }); }); if (n) setId(n); }}>Neue Vorlage</Knopf>
      </div>
      {v && <>
        <div className="raster-2 schmal">
          <Feld label="Bezeichnung"><input defaultValue={v.bezeichnung} onBlur={e => ausfuehren(() => quelle.speichern('lehrgang_vorlage', { id: v.id, bezeichnung: e.target.value }))} /></Feld>
          <Feld label="Standardlaufzeit (Wochen)"><input type="number" min={1} defaultValue={v.standard_wochen} onBlur={e => ausfuehren(() => quelle.speichern('lehrgang_vorlage', { id: v.id, standard_wochen: Number(e.target.value) }))} /></Feld>
        </div>
        <p className="leise klein">Änderungen an der Vorlage wirken nur auf neu angelegte Kurse. Bestehende Kurse haben ihren eigenen Lehrplan.</p>
        <LehrplanEditor key={v.id} tabelle="vorlage_knoten" elternFeld={{ vorlage_id: v.id }} />
      </>}
    </div>
  );
}

function Einrichtung() {
  const { stand, quelle, ausfuehren } = useDaten();
  const m = stand.mandant;
  return (
    <form className="formular schmal" onSubmit={e => e.preventDefault()}>
      <Feld label="Name"><input defaultValue={m.name} onBlur={e => ausfuehren(() => quelle.speichern('mandant', { name: e.target.value }), 'Gespeichert')} /></Feld>
      <Feld label="Organisationseinheit"><input defaultValue={m.einheit ?? ''} onBlur={e => ausfuehren(() => quelle.speichern('mandant', { einheit: e.target.value || null }), 'Gespeichert')} /></Feld>
      <Feld label="Logo (Adresse eines Bildes)" hinweis="Erscheint oben links und im Moodle-Stundenplan."><input type="url" defaultValue={m.logo_url ?? ''} onBlur={e => ausfuehren(() => quelle.speichern('mandant', { logo_url: e.target.value || null }), 'Gespeichert')} /></Feld>
      <Feld label="Hauptfarbe"><input type="color" defaultValue={m.farbe_primaer} onBlur={e => ausfuehren(() => quelle.speichern('mandant', { farbe_primaer: e.target.value }), 'Gespeichert')} /></Feld>
    </form>
  );
}
